import { useState } from 'react';
import clsx from 'clsx';
import type { CreateTaskInput, TaskStatus } from '../../api/organizer';

interface QuickAddTaskProps {
  status: TaskStatus;
  onCreate: (input: CreateTaskInput) => void;
  /** Compact variant for inside a board column. */
  compact?: boolean;
}

/**
 * One-line task capture.
 *
 * Stays open after submitting so a run of tasks can be typed without reaching
 * for the mouse — capturing several things at once is the normal case right
 * after a call.
 */
export function QuickAddTask({ status, onCreate, compact }: QuickAddTaskProps) {
  const [title, setTitle] = useState('');

  function submit() {
    const trimmed = title.trim();
    if (!trimmed) return;
    onCreate({ title: trimmed, status, insertAt: 'top' });
    setTitle('');
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className={clsx('flex items-center gap-2', compact ? 'px-1' : '')}
    >
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Add a task…"
        aria-label="New task title"
        className={clsx(
          'w-full rounded-lg border border-border-light bg-surface-primary text-text-primary placeholder:text-text-muted',
          'outline-none focus:border-text-primary',
          compact ? 'px-2.5 py-1.5 text-body-xs' : 'px-3 py-2 text-body-sm',
        )}
      />
      {title.trim() && (
        <button
          type="submit"
          className={clsx(
            'shrink-0 rounded-lg bg-text-primary font-medium text-text-inverse hover:opacity-90',
            compact ? 'px-2.5 py-1.5 text-body-xs' : 'px-3 py-2 text-body-sm',
          )}
        >
          Add
        </button>
      )}
    </form>
  );
}
